"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff } from "lucide-react";
import { useTranslations } from "next-intl";
import * as React from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import * as z from "zod";
import { useAuthDialogStore } from "@/features/user/ui/store";
import {
  AnalyticsEvents,
  identifyUser,
  trackEvent,
} from "@/lib/client/analytics/product-analytics";
import { authClient } from "@/lib/client/auth/client";
import {
  Box,
  Button,
  cn,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  HStack,
  Input,
  Text,
  VStack,
} from "@/ui";

// define form schema
export const formSchema = z
  .object({
    email: z.string().email().min(1).max(50),
    password: z.string().min(1),
    confirmPassword: z.string().min(1),
  })
  .superRefine(({ password, confirmPassword }, ctx) => {
    if (confirmPassword && password !== confirmPassword) {
      ctx.addIssue({
        code: "custom",
        message: "not_match",
        path: ["confirmPassword"],
      });
    }
  });

/**
 * SignUpForm component - Sign up form
 */
export const SignUpForm = () => {
  const toastT = useTranslations("toast");
  const generalT = useTranslations("general");
  const validationT = useTranslations("validation");
  const usersT = useTranslations("users");
  const [isLoading, setIsLoading] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);
  const [signUpError, setSignUpError] = React.useState<string>();
  const { toggleAuthDialog } = useAuthDialogStore();

  // 1. Define your form.
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
      confirmPassword: "",
    },
  });
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = form;
  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    setIsLoading(true);

    try {
      const result = await authClient.signUp.email({
        email: data.email,
        password: data.password,
        name: data.email.split("@")[0] ?? data.email, // Use email prefix as default name
      });

      if (result.error) {
        trackEvent(AnalyticsEvents.AUTH_SIGN_UP_FAILED, {
          error_code: result.error.code || "unknown",
          error_message: result.error.message,
        });
        setSignUpError(result.error.message);
      } else {
        const emailDomain = data.email.split("@")[1];
        identifyUser(data.email, { email: data.email, email_domain: emailDomain });
        trackEvent(AnalyticsEvents.AUTH_SIGN_UP_COMPLETED, {
          email: data.email,
          email_domain: emailDomain,
        });
        toast.success(toastT("success.sign_up"), {
          description: toastT("success.title"),
          duration: 5000,
        });
        reset();
        setSignUpError(undefined);
        toggleAuthDialog(false);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to sign up";
      trackEvent(AnalyticsEvents.AUTH_SIGN_UP_FAILED, {
        error_code: "exception",
        error_message: errorMessage,
      });
      setSignUpError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Form {...form}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="w-full"
      >
        <VStack className="w-full space-y-3">
          <Text className="min-h-4 text-center text-sm italic text-red-400">{signUpError}</Text>
          <FormField
            control={control}
            name="email"
            render={({ field }) => (
              <FormItem className="w-full space-y-1.5">
                <FormControl>
                  <Input
                    placeholder={generalT("email")}
                    {...field}
                  />
                </FormControl>
                <FormMessage>
                  {errors.email && validationT("required", { field: "email" })}
                </FormMessage>
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="password"
            render={({ field }) => (
              <FormItem className="w-full space-y-1.5">
                <FormControl>
                  <HStack className="items-center justify-end">
                    <Input
                      placeholder={generalT("password")}
                      {...field}
                      type={showPassword ? "text" : "password"}
                    />
                    <Box
                      onClick={handleTogglePassword}
                      className="absolute mr-2 cursor-pointer"
                    >
                      {showPassword ? <Eye className="h-5 w-5" /> : <EyeOff className="h-5 w-5" />}
                    </Box>
                  </HStack>
                </FormControl>
                <FormMessage>
                  {errors.password && validationT("required", { field: "password" })}
                </FormMessage>
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem className="w-full space-y-1.5">
                <FormControl>
                  <HStack className="items-center justify-end">
                    <Input
                      placeholder={generalT("confirm_password")}
                      {...field}
                      type={showPassword ? "text" : "password"}
                    />
                    <Box
                      onClick={handleTogglePassword}
                      className="absolute mr-2 cursor-pointer"
                    >
                      {showPassword ? <Eye className="h-5 w-5" /> : <EyeOff className="h-5 w-5" />}
                    </Box>
                  </HStack>
                </FormControl>
                <FormMessage>
                  {errors.confirmPassword && validationT("required", { field: "confirmPassword" })}
                </FormMessage>
              </FormItem>
            )}
          />
          <Button
            type="submit"
            variant="default"
            disabled={isLoading || !form.formState.isValid}
            className="mt-1 h-11 w-full"
          >
            <span className={cn(isLoading ? "loading loading-spinner" : "hidden")} />
            {usersT("sign_up")}
          </Button>
        </VStack>
      </form>
    </Form>
  );
};
